import React from 'react';

type ScoreChipSize = 'xs' | 'sm' | 'md';
type ScoreChipVariant = 'solid' | 'soft' | 'inline';

interface ScoreChipProps {
  /** Score value. If `max` is 5, treated as a 0-5 sub-rating; otherwise treated as 0-100. */
  value: number;
  max?: number;
  label?: string;
  size?: ScoreChipSize;
  variant?: ScoreChipVariant;
  title?: string;
  className?: string;
}

const bandFor100 = (v: number): { fg: string; bg: string; border: string } => {
  if (v >= 80) return { fg: '#86efac', bg: 'rgba(16, 185, 129, 0.14)', border: 'rgba(16, 185, 129, 0.35)' };
  if (v >= 65) return { fg: '#bef264', bg: 'rgba(132, 204, 22, 0.14)', border: 'rgba(132, 204, 22, 0.35)' };
  if (v >= 50) return { fg: '#fde68a', bg: 'rgba(251, 191, 36, 0.14)', border: 'rgba(251, 191, 36, 0.35)' };
  if (v >= 35) return { fg: '#fdba74', bg: 'rgba(249, 115, 22, 0.14)', border: 'rgba(249, 115, 22, 0.35)' };
  return { fg: '#fca5a5', bg: 'rgba(239, 68, 68, 0.14)', border: 'rgba(239, 68, 68, 0.35)' };
};

const bandFor5 = (v: number) => {
  // Convert 0-5 score to 0-100 band scale (5 → 90, 4 → 75, 3 → 60, 2 → 40, ≤1 → 25)
  const projected = v >= 5 ? 90 : v >= 4 ? 75 : v >= 3 ? 60 : v >= 2 ? 40 : 25;
  return bandFor100(projected);
};

const sizeStyles: Record<ScoreChipSize, React.CSSProperties> = {
  xs: { fontSize: 10, padding: '1px 5px', minWidth: 26, lineHeight: 1.4 },
  sm: { fontSize: 11, padding: '2px 7px', minWidth: 30, lineHeight: 1.4 },
  md: { fontSize: 12, padding: '3px 9px', minWidth: 36, lineHeight: 1.5 },
};

const ScoreChip: React.FC<ScoreChipProps> = ({
  value,
  max = 100,
  label,
  size = 'sm',
  variant = 'soft',
  title,
  className,
}) => {
  if (value == null || isNaN(value)) {
    return (
      <span
        className={className}
        style={{
          ...sizeStyles[size],
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-mono)',
          letterSpacing: '-0.02em',
        }}
        title={title}
      >
        —
      </span>
    );
  }

  const rounded = max === 5 ? Math.round(value * 10) / 10 : Math.round(value);
  // Color band: normalize to 0-100 scale for any max (e.g., dimension scores out of 50)
  const palette =
    max === 5 ? bandFor5(value) : bandFor100((value / max) * 100);

  const baseStyle: React.CSSProperties = {
    ...sizeStyles[size],
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    fontFamily: 'var(--font-mono)',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
    borderRadius: 4,
    color: palette.fg,
  };

  const variantStyle: React.CSSProperties =
    variant === 'inline'
      ? { background: 'transparent', border: 'none', padding: 0 }
      : variant === 'solid'
      ? { background: palette.fg, color: '#0a0a0a', border: `1px solid ${palette.fg}` }
      : { background: palette.bg, border: `1px solid ${palette.border}` };

  return (
    <span
      className={className}
      style={{ ...baseStyle, ...variantStyle }}
      title={title ?? (max === 5 ? `${rounded} / 5` : `CC Score ${rounded} / 100`)}
    >
      {label ? <span style={{ opacity: 0.75, fontWeight: 500 }}>{label}</span> : null}
      <span>{rounded}</span>
      {max !== 100 && max !== 5 ? <span style={{ opacity: 0.5 }}>/{max}</span> : null}
    </span>
  );
};

export default ScoreChip;
